import { baseApi } from "./base";
import { TAG_TYPE_PROJECT } from "./tags";

const apiWithTag = baseApi.enhanceEndpoints({
  addTagTypes: [TAG_TYPE_PROJECT],
});

// Constants for card preference types and pages
export const CARD_PREFERENCE_TYPE = {
  CUSTOMER: "customer",
  ADMIN: "admin",
} as const;

export const CARD_PREFERENCE_PAGE = {
  DASHBOARD: "dashboard",
  PROJECT: "project",
  PROJECT_EDIT: "project-edit",
} as const;

export type CardPreferenceType = typeof CARD_PREFERENCE_TYPE[keyof typeof CARD_PREFERENCE_TYPE];
export type CardPreferencePage = typeof CARD_PREFERENCE_PAGE[keyof typeof CARD_PREFERENCE_PAGE];

export interface CardPreferences {
  id?: number;
  company_id?: number;
  user_id?: number | null;
  project_id?: number | null;
  type?: CardPreferenceType;
  page?: CardPreferencePage;
  cardOrder: string[];
  hiddenCards: string[];
  details?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
  // Legacy fields for backward compatibility
  jobCountOrder?: string[];
  hiddenJobCount?: string[];
}

export interface CardPreferencesRequest {
  cardOrder: string[];
  hiddenCards?: string[];
  details?: Record<string, any>;
  userId?: number; // User ID for per-user preferences (optional)
  // Legacy fields for backward compatibility
  jobCountOrder?: string[];
  hiddenJobCount?: string[];
}

export interface GetCardPreferencesParams {
  type: CardPreferenceType;
  page: CardPreferencePage;
}

export interface UpdateCardPreferencesParams extends GetCardPreferencesParams {
  data: CardPreferencesRequest;
}

export const cardPreferencesApi = apiWithTag.injectEndpoints({
  endpoints: (builder) => ({
    getCardPreferences: builder.query<CardPreferences, GetCardPreferencesParams>({
      extraOptions: {
        isCompanyApi: true,
      },
      query: ({ type, page }) => ({
        url: `card-preferences/${type}/${page}`,
        method: "GET",
      }),
      providesTags: (result, error, args) => [
        { type: TAG_TYPE_PROJECT, id: `${args.type}-${args.page}` },
      ],
    }),
    updateCardPreferences: builder.mutation<
      CardPreferences,
      UpdateCardPreferencesParams
    >({
      extraOptions: {
        isCompanyApi: true,
      },
      query: ({ type, page, data }) => ({
        url: `card-preferences/${type}/${page}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, args) => [
        { type: TAG_TYPE_PROJECT, id: `${args.type}-${args.page}` },
      ],
    }),
    createCardPreferences: builder.mutation<
      CardPreferences,
      UpdateCardPreferencesParams
    >({
      extraOptions: {
        isCompanyApi: true,
      },
      query: ({ type, page, data }) => ({
        url: `card-preferences/${type}/${page}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, args) => [
        { type: TAG_TYPE_PROJECT, id: `${args.type}-${args.page}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCardPreferencesQuery,
  useUpdateCardPreferencesMutation,
  useCreateCardPreferencesMutation,
} = cardPreferencesApi;
